import React from 'react';
import camelcase from 'camelcase-es5';
import generateTextFieldIds, { isCharsExceeded } from './generateTextFieldIds';

import InputLabel from '../InputLabel';

import TextFieldFormControl from './TextFieldFormControl';
import TextFieldInput from './TextFieldInput';
import TextFieldFooter from './TextFieldFooter';
import TextFieldBase from './TextFieldBase';
import TextFieldPrefix from './TextFieldPrefix';
import useFormControlReducer from '../../hooks/useFormControlReducer';
import TPrefixedTextField from './@types/PrefixedTextField';

const PrefixedTextField = ({
  errorText,
  helperText,
  labelText = '',
  optionalText,
  prefixText,
  charsExceededText,
  charsRemainingText,
  autoComplete,
  step,
  value,
  inputRef,
  isRequired,
  onChange,
  onFocus,
  onBlur,
  onMouseOut,
  onMouseOver,
  onKeyPress,
  type = 'text',
  id = `PrefixedTextField-${camelcase(labelText)}`,
  maxLength,
  name,
  dataTestId,
  inputMode,
  ariaLabel,
  placeholder,
  isLive = true,
}: TPrefixedTextField) => {
  const { state, handlers } = useFormControlReducer({
    onChange,
    onFocus,
    onBlur,
    onMouseOut,
    onMouseOver,
    errorText,
    value,
  });

  const { isDirty, isEmpty, isErrored, isFocused, characterCount } = state;
  const { handleChange, handleFocus, handleBlur, handleMouseOver, handleMouseOut } = handlers;

  const charsExceeded = isCharsExceeded(characterCount, maxLength);
  const { helperTextId, errorTextId, charsExceededId } = generateTextFieldIds(id, Boolean(errorText), charsExceeded);

  return (
    <TextFieldBase kind="prefixed">
      {labelText && (
        <InputLabel htmlFor={id} isInputRequired={isRequired} labelText={labelText} optionalText={optionalText} />
      )}
      <TextFieldFormControl isFocused={isFocused} isErrored={isErrored} isDirty={isDirty} isEmpty={isEmpty}>
        <TextFieldPrefix>{prefixText}</TextFieldPrefix>
        <TextFieldInput
          id={id}
          name={name}
          ref={inputRef}
          isRequired={isRequired}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          onKeyPress={onKeyPress}
          type={type}
          step={step}
          autoComplete={autoComplete}
          inputMode={inputMode}
          data-testid={dataTestId}
          ariaLabel={ariaLabel}
          placeholder={placeholder}
          helperTextId={helperTextId}
          errorTextId={errorTextId}
          charsExceededId={charsExceededId}
        />
      </TextFieldFormControl>
      <TextFieldFooter
        maxLength={maxLength}
        helperText={helperText}
        characterCount={characterCount}
        errorText={errorText}
        charsExceededText={charsExceededText}
        charsRemainingText={charsRemainingText}
        isLive={isLive}
        id={helperTextId}
        errorTextId={errorTextId}
        charsExceededId={charsExceededId}
        charsExceeded={charsExceeded}
      />
    </TextFieldBase>
  );
};

export default PrefixedTextField;
